/**
 * Packages in the chef print packet's words (PROD-1500, PROD-1502; Abner's
 * "Target Print", Figma 137:24 and 151:2): what each printed row says about
 * the containers the food rides in.
 *
 * - Summary rows: the grey band is the package description, one line per
 *   container size ("16x Standard Hotel Pans w/ 20") and one for the servings
 *   left over ("+ 5 EA", D54); individually packaged food is just the count
 *   ("12x Containers with Clear Lid (24 oz)"); bulk food with no plan reads the
 *   container, plural, with no count.
 * - Delivery route rows: [check] / "17 Standard Hotel Pans" / "325x Beef Soft
 *   Taco", one row per item.
 * - Recipe pages: a Packaging table per campus and mealtime, one row per bulk
 *   container ("1." / "Standard Hotel Pan" / "20x servings"), one row per kind
 *   of individual container with ↳ lines for what goes in it.
 * - Ingredient list: the containers the packing plans need, summed across
 *   campuses, with the campuses' packaging and supplies.
 * - Milk: its own block per campus, "Provide supply for 5 days".
 *
 * Every count comes from the bulk packing plans `shipmentsToCampuses` attached
 * (`Deliverable.packing`, planned once by `@ordo/constants/packing-plan` from
 * the catalog's package sizes and portion volumes); this module never plans
 * again, only splits the plans into lines. Pure; unit-tested in
 * `test/lib/chefs/print-packages.test.ts`.
 */

import { formatFractionalValue } from '@ordo/constants/measure-format'
import {
  type BulkPackingPlan,
  bulkPackingSpecKey,
  containerNameFor,
  containerRuns,
  type ExtraMeasure,
  extraServingsParts,
} from '@ordo/constants/packing-plan'

import { CHEF_SERVICE_MATERIALS_VISIBLE } from '../../../../shared/chef-packaging-visibility'
import { deliverableMode } from './deliverable-copy'
import { serviceDayPhrase, stopUnloadSections } from './delivery-route'
import { temperatureLabel } from './format'
import { campusMilkSupplyDays, milkGroupBreakdown, milkMealtimeBreakdown } from './milk'
import { packingCountLabel } from './packing'
import { pluralizePackagingName } from './plural'
import { recipeKey } from './print-plan'
import { deliverableTags } from './print-words'
import { sortDeliverables } from './totals'
import type { Campus, CampusMilkLine, Component, Deliverable } from './types'
import { MEALTIME_LABEL, MEALTIME_ORDER } from './types'

// ---------------------------------------------------------------------------
// Package lines
// ---------------------------------------------------------------------------

/** One line of a row's package band: "**15x** Standard Hotel Pans w/ **20**", "**+ 5** EA". */
export interface PrintPackageLine {
  /** "15x", "+ 5"; null for a line with no count ("Standard Hotel Pans", "Packed with …"). */
  count: string | null
  /** "Standard Hotel Pans", "Containers with Clear Lid (24 oz)". */
  name: string
  /** The servings each of these containers holds ("20"), printed after "w/"; null when it says none. */
  fill: string | null
  /** A SECTION: an inner package that rides inside the line above it ("60x Kraft Sandwich Bags — Muffin"). */
  section?: true
}

/** The line as plain words: "15x Standard Hotel Pans w/ 20"; a section leads with "› ". */
export function printPackageLineText(line: PrintPackageLine): string {
  const body = [line.count, line.name].filter(Boolean).join(' ')
  const text = line.fill !== null ? `${body} w/ ${line.fill}` : body
  return line.section ? `› ${text}` : text
}

/**
 * The inner packages of an individually packaged deliverable, one line each,
 * counted for the whole row: 60 bags with two orange cups each is "120x Paper
 * Food Cups with Lid (8 oz) — Orange Wedges". Food the outer package holds
 * directly needs no line; the portion column already lists every food.
 */
export function sectionPackageLines(d: Pick<Deliverable, 'sections' | 'qty'>): PrintPackageLine[] {
  return (d.sections ?? []).flatMap((section) => {
    if (!section.packaging) {
      return []
    }
    const count = d.qty * section.perMeal
    return [
      {
        count: `${count.toLocaleString()}x`,
        name: `${pluralizePackagingName(section.packaging, count)} — ${section.items.map((item) => item.name).join(' + ')}`,
        fill: null,
        section: true as const,
      },
    ]
  })
}

function servingsWord(servings: number): string {
  return String(Math.round(servings * 100) / 100)
}

/**
 * Containers as one line per run of equal fills, in plan order (fullest
 * first), the way the shared `formatContainerRuns` reads them: [20 x16] →
 * "16x Standard Hotel Pans w/ 20"; [144 x4, 120] → "4x Bulk Delivery Boxes w/
 * 144" and "1x Bulk Delivery Box w/ 120". Joined with ", " the lines ARE that
 * description.
 */
export function containerRunLines(containers: ReadonlyArray<number>, containerName: string): PrintPackageLine[] {
  return containerRuns(containers).map((run) => ({
    count: `${run.count}x`,
    name: containerNameFor(run.count, containerName),
    fill: servingsWord(run.servings),
  }))
}

/**
 * The servings a plan leaves over once its containers are full, as their own line, in the food's own unit:
 * "+ 2.5 C", "+ 3 hamburgers", "+ 5 EA" (D54; never rounded up to another container).
 */
export function extraServingsLine(extra: number, measure?: ExtraMeasure | null): PrintPackageLine | null {
  const parts = extraServingsParts(extra, measure)
  return parts ? { count: `+ ${parts.count}`, name: parts.word, fill: null } : null
}

/** A row's plans as package lines: each plan's runs, then its leftover servings, in plan order. */
export function packingLines(plans: ReadonlyArray<BulkPackingPlan>): PrintPackageLine[] {
  return plans.flatMap((plan) => {
    const extra = extraServingsLine(plan.extra, plan.extraMeasure)
    return [...containerRunLines(plan.containers, plan.container.name), ...(extra ? [extra] : [])]
  })
}

function plainLine(name: string): PrintPackageLine {
  return { count: null, name, fill: null }
}

/**
 * What a summary row's grey band says about its packages, once:
 * - bulk food with a packing plan: one line per container size;
 * - bulk food counted on another row: "Packed with Corn, Seasoned";
 * - individually packaged food: the count, "12x Containers with Clear Lid (24 oz)",
 *   then one line per SECTION inside it (`sectionPackageLines`);
 * - bulk food with no plan: the container's name, plural, with no count;
 * - loose food (no package at all): nothing.
 */
export function summaryPackageLines(
  d: Pick<Deliverable, 'packing' | 'packedWith' | 'packaging' | 'bulk' | 'packagingSkuKind' | 'qty' | 'sections'>,
): PrintPackageLine[] {
  if (d.packing && d.packing.length > 0) {
    return packingLines(d.packing)
  }
  if (d.packedWith && d.packedWith.length > 0) {
    return [plainLine(`Packed with ${d.packedWith.join(', ')}`)]
  }
  const mode = deliverableMode(d)
  if (mode === 'containers' && d.packaging) {
    return [
      {
        count: `${d.qty.toLocaleString()}x`,
        name: pluralizePackagingName(d.packaging, d.qty),
        fill: null,
      },
      ...sectionPackageLines(d),
    ]
  }
  if (mode === 'bulk' && d.packaging) {
    // No plan: the vessel, plural and with no count, as the card's band says it.
    return [plainLine(pluralizePackagingName(d.packaging, 2))]
  }
  return []
}

/**
 * The package a milk row names as its own, or null when it has none. A BULK
 * package is never one: a bulk-mode milk card ships in a pan or the Bulk
 * Delivery Box only because a bulk card needs a package (the Menus builder
 * falls back to a bulk pan for a "No Packaging / Bulk" card), and milk takes no
 * room in it (D25). Abner, Oct 20, on the milk page: "it says to deliver milk in
 * a hotel pan, which doesn't make sense. If there's no explicit outer packaging
 * for the milk, don't make assumptions."
 */
export function milkOuterPackaging(line: Pick<CampusMilkLine, 'packaging' | 'packagingSkuKind'>): string | null {
  return line.packaging && line.packagingSkuKind !== 'BULK' ? line.packaging : null
}

/** A milk row's package band: its plan's lines, else the jug or carton it ships in, else nothing. */
export function milkPackageLines(
  line: Pick<CampusMilkLine, 'packing' | 'packaging' | 'packagingSkuKind'>,
): PrintPackageLine[] {
  if (line.packing && line.packing.length > 0) {
    return packingLines(line.packing)
  }
  const outer = milkOuterPackaging(line)
  return outer ? [plainLine(outer)] : []
}

// ---------------------------------------------------------------------------
// Summary rows
// ---------------------------------------------------------------------------

/** A summary row in the target print's words. */
export interface PrintSummaryRowWords {
  /** "330x": the servings (or containers, for individually packaged meals). */
  qty: string
  /** The item as the catalog names it ("Creamy Cheddar Mac (White Penne) • Steamed Seasonal Veggie"). */
  name: string
  /** "1/2 cup per serving", on the right; null on individually packaged meals and rows with no serving. */
  perServing: string | null
  packages: PrintPackageLine[]
  /** The condiments line, when the row's components do not list the condiments themselves. */
  notes: string[]
  /** "K-8 portion", "Field trip", the dry-run label. */
  tags: string[]
  /** "Hot", "Chilled", "Ambient". */
  temperature: string
}

/**
 * A deliverable as the summary sheet prints it (Abner, 137:1722: "Change
 * subtitles on items row to be the packages description"). The package is
 * said once, in the band; the serving moves to the right of the name.
 */
export function summaryRowWords(d: Deliverable): PrintSummaryRowWords {
  const unitized = deliverableMode(d) === 'containers'
  const notes = d.condiments && !d.components.some((c) => c.isCondiment) ? [d.condiments] : []
  return {
    qty: `${d.qty.toLocaleString()}x`,
    name: d.name,
    perServing: !unitized && d.servingSize ? `${d.servingSize} per serving` : null,
    packages: summaryPackageLines(d),
    notes,
    tags: deliverableTags(d),
    temperature: temperatureLabel(d.temperature),
  }
}

/** A milk row on the summary's Milk block, in the same shape as a food row. */
export function milkRowWords(line: CampusMilkLine): PrintSummaryRowWords {
  return {
    qty: `${line.qty.toLocaleString()}x`,
    name: line.name,
    perServing: line.servingSize ? `${line.servingSize} per serving` : null,
    packages: milkPackageLines(line),
    notes: [],
    tags: [],
    temperature: temperatureLabel(line.temperature),
  }
}

/** "Provide supply for 5 days" / "Provide supply for 1 day" (Abner, 164:875). Never fewer than one day. */
export function milkSupplyLine(days: number): string {
  const n = Math.max(1, days)
  return `Provide supply for ${n} ${n === 1 ? 'day' : 'days'}`
}

// ---------------------------------------------------------------------------
// Delivery route rows
// ---------------------------------------------------------------------------

/**
 * The route row's Package cell (Abner, 137:1699: "[check] / X Packages (name
 * of package) / Content"): "17 Standard Hotel Pans" (its plans' containers,
 * counted), "12 Containers with Clear Lid (24 oz)", "Standard Hotel Pans" (bulk
 * with no plan: no count), or "Packed with Corn, Seasoned".
 */
export function routePackageLabel(
  d: Pick<Deliverable, 'packing' | 'packedWith' | 'packaging' | 'bulk' | 'packagingSkuKind' | 'qty'>,
): string {
  if (d.packing && d.packing.length > 0) {
    return packingCountLabel(d.packing)
  }
  if (d.packedWith && d.packedWith.length > 0) {
    return `Packed with ${d.packedWith.join(', ')}`
  }
  const mode = deliverableMode(d)
  if (mode === 'containers' && d.packaging) {
    return `${d.qty.toLocaleString()} ${pluralizePackagingName(d.packaging, d.qty)}`
  }
  return mode === 'bulk' && d.packaging ? pluralizePackagingName(d.packaging, 2) : ''
}

/** A milk row's Package cell: its plan's containers, counted, else the jug or carton name, else blank. */
export function routeMilkPackageLabel(
  line: Pick<CampusMilkLine, 'packing' | 'packaging' | 'packagingSkuKind'>,
): string {
  return line.packing && line.packing.length > 0 ? packingCountLabel(line.packing) : (milkOuterPackaging(line) ?? '')
}

/** The route row's Content cell: "335x Corn, Seasoned". */
export function routeContentLabel(qty: number, name: string): string {
  return `${qty.toLocaleString()}x ${name}`
}

/** One row of a stop's "What to deliver": [check] | Package | Content | meal. */
export interface RouteDeliverRow {
  key: string
  /** "17 Standard Hotel Pans", "12 Containers with Clear Lid (24 oz)"; "" for none. */
  packageLabel: string
  /** "325x Beef Soft Taco". */
  content: string
  /** "Lunch", "Breakfast for Mon", "Breakfast 5 · Lunch 5"; null when the stop needs none. */
  tag: string | null
}

export interface RouteDeliverGroup {
  /** "milk", or the temperature. */
  key: string
  /** "Milk", "Hot", "Chilled", "Ambient". */
  label: string
  /** "Provide supply for 5 days" on the milk group; null on the others. */
  note: string | null
  rows: RouteDeliverRow[]
}

/**
 * A stop's "What to deliver" in the target print's words (Abner, 137:1699):
 * milk first as its own group with "Provide supply for X days" (164:875),
 * then the food by temperature so the van loads by carrier (`stopUnloadSections`),
 * each row [check] / packages / content, the meal on the right.
 */
export function routeDeliverGroups(campus: Campus | undefined, deliveryDate: string): RouteDeliverGroup[] {
  if (!campus) {
    return []
  }
  const groups: RouteDeliverGroup[] = []
  const milk = campus.milk ?? []
  if (milk.length > 0) {
    groups.push({
      key: 'milk',
      label: 'Milk',
      note: milkSupplyLine(campusMilkSupplyDays(campus)),
      rows: milk.map((line) => ({
        key: `milk-${line.key}`,
        packageLabel: routeMilkPackageLabel(line),
        content: routeContentLabel(line.qty, line.name),
        tag: [milkMealtimeBreakdown(line), milkGroupBreakdown(line)].filter(Boolean).join(' — ') || null,
      })),
    })
  }
  for (const section of stopUnloadSections(campus, deliveryDate)) {
    groups.push({
      key: section.temperature,
      label: section.label,
      note: null,
      rows: section.rows.map((row) =>
        row.kind === 'deliverable'
          ? {
              key: row.key,
              packageLabel: routePackageLabel(row.deliverable),
              content: routeContentLabel(row.deliverable.qty, row.deliverable.name),
              tag: row.tag,
            }
          : { key: row.key, packageLabel: '', content: routeContentLabel(row.line.qty, row.line.name), tag: row.tag },
      ),
    })
  }
  return groups
}

// ---------------------------------------------------------------------------
// Recipe pages: the Packaging section
// ---------------------------------------------------------------------------

/** "↳ 1 1/2 cup Creamy Cheddar Mac (White Penne)": what goes in each container. */
export interface RecipePackagingContent {
  /** "1 1/2 cup", "1 piece", "1"; null when the food has no portion. */
  amount: string | null
  name: string
}

export interface RecipePackagingRow {
  key: string
  /** "1." for a numbered bulk container, "12x" for individual containers, "" for one with no count. */
  count: string
  /** "Standard Hotel Pan", "Containers with Clear Lid (24 oz)". */
  packageType: string
  /** "20x servings"; null on an individual container row (its `each` lines say it). */
  servings: string | null
  /** Individual containers: one line per food in each. Empty otherwise. */
  each: RecipePackagingContent[]
}

export interface RecipePackagingTable {
  key: string
  campusName: string
  /** "Lunch", "Breakfast, serve Mon". */
  mealtimeLabel: string
  /** "Hot", "Chilled", "Ambient". */
  temperature: string
  rows: RecipePackagingRow[]
}

const COUNT_UNITS = new Set(['each', 'ea', 'x', ''])

/** A component's portion in each container: "1 1/2 cup", "1 piece", "1". */
export function portionAmount(component: Pick<Component, 'portion'>): string | null {
  const portion = component.portion
  if (!portion || !Number.isFinite(portion.quantity) || portion.quantity <= 0) {
    return null
  }
  const amount = formatFractionalValue(portion.quantity)
  const unit = portion.unit.trim()
  return COUNT_UNITS.has(unit.toLowerCase()) ? amount : `${amount} ${unit}`
}

function servingsLabel(servings: number): string {
  return `${servingsWord(servings)}x servings`
}

/**
 * One numbered row per container of each plan, fullest first, numbered from 1 within the plan, then a "+" row for
 * the servings it leaves over ("Items remaining", "5 EA", D54).
 */
function bulkRows(plans: ReadonlyArray<BulkPackingPlan>, keyBase: string): RecipePackagingRow[] {
  return plans.flatMap((plan) => {
    const key = `${keyBase}|${bulkPackingSpecKey(plan)}`
    const rows: RecipePackagingRow[] = plan.containers.map((servings, i) => ({
      key: `${key}|${i + 1}`,
      count: `${i + 1}.`,
      packageType: plan.container.name,
      servings: servingsLabel(servings),
      each: [],
    }))
    const extra = extraServingsParts(plan.extra, plan.extraMeasure)
    if (extra) {
      rows.push({
        key: `${key}|extra`,
        count: '+',
        packageType: 'Items remaining',
        servings: `${extra.count} ${extra.word}`,
        each: [],
      })
    }
    return rows
  })
}

/** "Breakfast, serve Mon" when the food is served on another day than it is delivered; else "Breakfast". */
function mealtimeWords(label: string, serveDays: readonly string[] | undefined, deliveryIso: string): string {
  const phrase = serviceDayPhrase(serveDays, deliveryIso)
  return phrase ? `${label}, ${phrase.replace(/^for /, 'serve ')}` : label
}

/**
 * The recipe page's Packaging section (Abner, 137:2055): one table per campus
 * x mealtime x temperature that serves this recipe, in the campuses' order.
 *
 * - A bulk deliverable with a plan: one numbered row per container, with its
 *   servings, on the page of the deliverable's FIRST recipe-backed food only,
 *   so a two-recipe deliverable never prints its pans twice.
 * - Individually packaged meals: one row per kind of container, the count and
 *   ↳ lines for every food in it ("For individually packaged we can't show 1
 *   row for each package because it will get too long, so just use count").
 * - A row counted in another row's containers ("packed with") adds nothing.
 * - Bulk food with no plan: one row, the container with the servings.
 * - Loose food (no package) adds nothing.
 */
export function recipePackagingTables(
  campuses: readonly Campus[],
  key: string,
  deliveryIso: string,
): RecipePackagingTable[] {
  const tables: RecipePackagingTable[] = []
  for (const campus of campuses) {
    const blocks = [...campus.mealtimes].sort(
      (a, b) => MEALTIME_ORDER.indexOf(a.mealtime) - MEALTIME_ORDER.indexOf(b.mealtime),
    )
    for (const block of blocks) {
      const byTemperature = new Map<string, RecipePackagingTable>()
      for (const d of sortDeliverables(block.deliverables)) {
        const component = d.components.find((c) => c.recipe && recipeKey(c.name, d.unit) === key)
        if (!component) {
          continue
        }
        const rowKey = `${campus.id}|${d.id}|${component.id}`
        let rows: RecipePackagingRow[]
        if (d.packing && d.packing.length > 0) {
          rows = d.components.find((c) => c.recipe)?.id === component.id ? bulkRows(d.packing, rowKey) : []
        } else if (d.packedWith && d.packedWith.length > 0) {
          // Counted in the containers of the row it is packed with.
          rows = []
        } else if (deliverableMode(d) === 'containers' && d.packaging) {
          rows = [
            {
              key: rowKey,
              count: `${d.qty.toLocaleString()}x`,
              packageType: pluralizePackagingName(d.packaging, d.qty),
              servings: null,
              each: d.components.filter((c) => !c.isCondiment).map((c) => ({ amount: portionAmount(c), name: c.name })),
            },
          ]
        } else if (d.packaging) {
          rows = [
            {
              key: rowKey,
              count: '',
              packageType: d.packaging,
              servings: servingsLabel(d.qty),
              each: [],
            },
          ]
        } else {
          rows = []
        }
        if (rows.length === 0) {
          continue
        }
        const temperature = temperatureLabel(d.temperature)
        let table = byTemperature.get(temperature)
        if (!table) {
          table = {
            key: `${campus.id}|${block.mealtime}|${d.temperature}`,
            campusName: campus.name,
            mealtimeLabel: mealtimeWords(MEALTIME_LABEL[block.mealtime], block.serveDays, deliveryIso),
            temperature,
            rows: [],
          }
          byTemperature.set(temperature, table)
          tables.push(table)
        }
        table.rows.push(...rows)
      }
    }
  }
  return tables
}

// ---------------------------------------------------------------------------
// Ingredient list: the Supplies group
// ---------------------------------------------------------------------------

/** One row of the ingredient list's Supplies group: "32x Standard Hotel Pan". */
export interface PacketSupplyRow {
  key: string
  name: string
  qty: number
  isCompostable: boolean
}

/**
 * The Supplies group at the end of the ingredient list: the campuses'
 * packaging and supplies, plus the containers every bulk packing plan needs
 * ("32x Standard Hotel Pan"), summed by SKU across campuses and days. A plan
 * with servings left over counts one container more: the kitchen puts them in a
 * container with room or in another (D54), and the list says what to have on
 * hand. Bulk food with no plan adds nothing: its container count is not known.
 */
export function packetSupplyRows(campuses: readonly Campus[]): PacketSupplyRow[] {
  const rows = new Map<string, PacketSupplyRow>()
  const add = (key: string, name: string, qty: number, isCompostable: boolean) => {
    if (qty <= 0) {
      return
    }
    const groupKey = JSON.stringify([key, isCompostable])
    const row = rows.get(groupKey)
    if (row) {
      row.qty += qty
    } else {
      rows.set(groupKey, { key: groupKey, name, qty, isCompostable })
    }
  }
  for (const campus of campuses) {
    // Service materials are held with the chef supplies UI (`CHEF_SERVICE_MATERIALS_VISIBLE`); containers stay.
    for (const line of [...campus.packaging, ...(CHEF_SERVICE_MATERIALS_VISIBLE ? campus.supplies : [])]) {
      add(line.skuId, line.name, line.qty, line.isCompostable)
    }
    const plans = [
      ...campus.mealtimes.flatMap((block) => block.deliverables.flatMap((d) => d.packing ?? [])),
      ...(campus.milk ?? []).flatMap((line) => line.packing ?? []),
    ]
    for (const plan of plans) {
      add(plan.container.skuId, plan.container.name, plan.containerCount + (plan.extra > 0 ? 1 : 0), false)
    }
  }
  return [...rows.values()].sort((a, b) => a.name.localeCompare(b.name))
}
